import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { supabase } from '../lib/supabase';
import { useRecaptcha } from '../contexts/RecaptchaContext';

export function useRecaptchaVerification() {
  const { isEnabled } = useRecaptcha();
  const { executeRecaptcha } = isEnabled ? useGoogleReCaptcha() : { executeRecaptcha: null };
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyRecaptcha = async (): Promise<boolean> => {
    // If reCAPTCHA is not enabled, skip verification
    if (!isEnabled) {
      return true;
    }

    if (!executeRecaptcha) {
      console.warn('reCAPTCHA not loaded yet');
      setError('reCAPTCHA is still loading. Please wait a moment and try again.');
      return false;
    }

    try {
      setVerifying(true);
      setError(null);

      // Execute reCAPTCHA and get token
      const token = await executeRecaptcha('login_register');

      // Verify token with Supabase Edge Function
      const { data, error: verifyError } = await supabase.functions.invoke('verify-recaptcha', {
        body: { token }
      });

      if (verifyError) {
        console.error('reCAPTCHA verification error:', verifyError);
        setError('Failed to verify reCAPTCHA. Please try again.');
        return false;
      }

      if (!data?.success) {
        console.error('reCAPTCHA verification failed:', data);
        const errorMsg = data?.errors ? `reCAPTCHA error: ${data.errors.join(', ')}` : 'reCAPTCHA verification failed. Please try again.';
        setError(errorMsg);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error during reCAPTCHA verification:', err);
      setError('An error occurred during verification. Please try again.');
      return false;
    } finally {
      setVerifying(false);
    }
  };

  return { verifyRecaptcha, verifying, error };
}